import React from 'react';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { Code2, Key, BookOpen, Webhook } from 'lucide-react';

interface Props { onBack: () => void; }

export default function ApiPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  const sections = [
    { icon: <Key size={24} />, title: en ? 'Authentication' : '认证', desc: en ? 'Secure API key-based authentication with rate limiting and usage tracking.' : '基于 API Key 的安全认证，支持速率限制和用量追踪。' },
    { icon: <Webhook size={24} />, title: en ? 'RESTful Endpoints' : 'RESTful 接口', desc: en ? 'Clean, well-documented REST API for transcription, analysis, and export.' : '简洁、文档完善的 REST API，支持转录、分析和导出。' },
    { icon: <Code2 size={24} />, title: en ? 'SDKs & Libraries' : 'SDK 和库', desc: en ? 'Official SDKs for Python, JavaScript, and more. Get started in minutes.' : '官方提供 Python、JavaScript 等 SDK，几分钟即可上手。' },
    { icon: <BookOpen size={24} />, title: en ? 'Documentation' : '文档', desc: en ? 'Comprehensive guides, API reference, and code examples for every endpoint.' : '全面的指南、API 参考和每个接口的代码示例。' },
  ];

  return (
    <StaticPageLayout title="API" onBack={onBack}>
      <div className="text-center mb-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-900/10 mb-6">
          <Code2 size={32} className="text-brand-900" />
        </div>
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Developer API' : '开发者 API'}
        </h2>
        <p className="text-lg text-ink-secondary max-w-2xl mx-auto">
          {en ? 'Integrate Minutes AI into your workflow with our powerful API. Build custom integrations and automate your meeting intelligence pipeline.' : '通过强大的 API 将 Minutes AI 集成到您的工作流中。构建自定义集成，自动化您的会议智能流水线。'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {sections.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-surface-border p-8">
            <div className="text-brand-700 mb-4">{s.icon}</div>
            <h3 className="text-xl font-bold text-brand-900 mb-2">{s.title}</h3>
            <p className="text-ink-secondary">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* Code example */}
      <div className="bg-brand-900 rounded-2xl p-8 text-white">
        <h3 className="text-lg font-bold mb-4">{en ? 'Quick Start' : '快速开始'}</h3>
        <pre className="text-sm text-brand-200 overflow-x-auto"><code>{`curl -X POST https://api.minutesai.com/v1/transcribe \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "file=@meeting.mp3" \\
  -F "language=auto"`}</code></pre>
      </div>
    </StaticPageLayout>
  );
}
